import React, { FunctionComponent, useCallback, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { MdCheck, MdPlayArrow } from 'react-icons/md'
import * as Yup from 'yup'
import { Grid } from '@material-ui/core'

import Button from 'shared/components/Button'
import Modal from 'shared/components/Modal'
import ModalSelectFrotista from 'shared/components/ModalSelectFrotista'
import Scaffold from 'shared/components/ScaffoldNew'
import SnackBar from 'shared/components/SnackBar'
import Text from 'shared/components/Text'
import { registerEvent } from 'shared/services/TrackingGAService'
import { colors } from 'shared/styles/colors'
import dimensions from 'shared/styles/dimensions'
import getValidationErrors, {
  IValidationErrors,
} from 'shared/utils/getValidationErrors'
import Formatter, { phoneMask } from 'shared/utils/formatters'
import IMotoristaReferencia from 'modules/motorista/domain/entities/IMotoristaReferencia'
import { useFrotista } from 'modules/user/hooks/useFrotista'

import { useMotoristas } from '../../../../hooks/useMotoristas'
import { IReferenciasProfissionaisProps } from './ReferenciasProfissionais.types'
import {
  Container,
  ContainerModal,
  StyledForm,
  StyledInput,
  Title,
  StyledCheck,
  SubText,
  TextModal,
} from './styles'
import { FormikProps, FormikValues } from 'formik'

interface IFormProps {
  nomeObrigatorio: string
  telefoneObrigatorio: string
  nomeOpcional: string
  telefoneOpcional: string
}

const ReferenciasProfissionais: FunctionComponent<IReferenciasProfissionaisProps> =
  ({ createMotorista }) => {
    const formRef = useRef<FormikProps<FormikValues>>()
    const history = useHistory()
    const { motoristas, setMotoristas, handleBackButton, loading, setLoading } =
      useMotoristas()
    const { frotistaId } = useFrotista()
    const [errorMessageForm, setErrorMessageForm] = useState({} as IFormProps)
    const [errorMessageSnackBar, setErrorMessageSnackBar] = useState('')
    const [showingError, setShowingError] = useState(false)
    const [openModal, setOpenModal] = useState(false)
    const [openModalSelectFrotista, setOpenModalSelectFrotista] =
      useState(false)

    const clearValidationMessages = () => {
      setErrorMessageForm({
        nomeObrigatorio: '',
        telefoneObrigatorio: '',
        nomeOpcional: '',
        telefoneOpcional: '',
      })
    }

    const showValidationErrors = (errors: IValidationErrors) => {
      setErrorMessageForm({
        nomeObrigatorio: errors.nomeObrigatorio,
        telefoneObrigatorio: errors.telefoneObrigatorio,
        nomeOpcional: errors.nomeOpcional,
        telefoneOpcional: errors.telefoneOpcional,
      })
    }

    const handleCloseModal = useCallback(async () => {
      setOpenModal(false)

      history.push('/motoristas')
    }, [history])

    const handleRegister = useCallback(async () => {
      const {
        nomeOpcional,
        nomeObrigatorio,
        telefoneOpcional,
        telefoneObrigatorio,
      } = formRef.current.values

      const motorista_referencia: IMotoristaReferencia[] = [
        {
          nome: nomeObrigatorio,
          telefone: telefoneObrigatorio.replace(Formatter.somenteNumeros(), ''),
        },
      ]

      if (nomeOpcional && telefoneOpcional) {
        motorista_referencia.push({
          nome: nomeOpcional,
          telefone: telefoneOpcional.replace(Formatter.somenteNumeros(), ''),
        })
      }

      setMotoristas({
        motorista_referencia,
      })
      registerEvent({
        categoria: 'Cadastro de Motorista',
        acao: 'Click Salvar ',
        local: 'Tela final do cadastro de motorista',
      })

      if (!frotistaId) {
        setOpenModalSelectFrotista(true)
        return
      }

      setLoading(true)
      try {
        const schema = Yup.object().shape(
          {
            nomeObrigatorio: Yup.string().required('Nome é obrigatório!'),
            telefoneObrigatorio: Yup.string()
              .required('Telefone é obrigatório!')
              .trim()
              .min(13, 'Telefone inválido!')
              .max(15, 'Telefone inválido!'),
            nomeOpcional: Yup.string().when('telefoneOpcional', {
              is: val => val?.length > 0,
              then: Yup.string().required('Nome é obrigatório!'),
            }),
            telefoneOpcional: Yup.string().when('nomeOpcional', {
              is: val => val?.length > 0,
              then: Yup.string()
                .trim()
                .required('Telefone é obrigatório!')
                .min(13, 'Telefone inválido!')
                .max(15, 'Telefone inválido!'),
            }),
          },
          [
            ['nomeObrigatorio', 'telefoneObrigatorio'],
            ['nomeOpcional', 'telefoneOpcional'],
          ],
        )

        await schema.validate(
          {
            nomeObrigatorio,
            telefoneObrigatorio,
            nomeOpcional,
            telefoneOpcional,
          },
          {
            abortEarly: false,
          },
        )

        clearValidationMessages()

        await createMotorista.execute({
          endereco: {
            cep: motoristas.endereco.cep,
            bairro: motoristas.endereco.bairro,
            cidade: motoristas.endereco.cidade,
            cidade_ibge: motoristas.endereco.cidade_ibge,
            estado: motoristas.endereco.estado,
            logradouro: motoristas.endereco.logradouro,
            numero: motoristas.endereco.numero,
            complemento: motoristas.endereco.complemento,
          },
          telefone: motoristas.telefone,
          doc_imagem_frente: motoristas.doc_imagem_frente,
          doc_imagem_verso: motoristas.doc_imagem_verso,
          frotista_id: frotistaId,
          motorista_referencia,
          email: motoristas.email,
        })

        setLoading(false)
        setOpenModal(true)
      } catch (err: unknown) {
        setLoading(false)
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err)
          showValidationErrors(errors)
          return
        } else if (err instanceof Error) {
          setErrorMessageSnackBar(err.message)
        } else {
          setErrorMessageSnackBar('Erro ao tentar criar novo motorista')
        }
        setShowingError(true)
      }
    }, [motoristas, createMotorista, frotistaId])

    return (
      <Scaffold
        showFooterScaffold
        titleBackButton="Voltar"
        onClickBackButton={handleBackButton}
        icon={MdPlayArrow}
        titleNextButton="Salvar"
        onClickNextButton={handleRegister}
        isNextButtonDisabled={false}
        color={colors.green}
        loading={loading}
        titleHeader="Novo motorista"
      >
        {openModal && (
          <Modal size="small" openModal={openModal} showCloseButton={false}>
            <ContainerModal>
              <StyledCheck />

              <div>
                <TextModal>Solicitação de cadastro</TextModal>
                <TextModal>efetuada com sucesso!</TextModal>
                <SubText>
                  Estamos analisando os dados e em breve retornaremos.
                </SubText>
              </div>

              <Button color={colors.orange} onClick={handleCloseModal}>
                <MdCheck /> Concluir
              </Button>
            </ContainerModal>
          </Modal>
        )}
        <Container>
          <Title>Referência profissional</Title>
          <Grid
            container
            style={{
              marginBottom: dimensions.spaces.small,
              marginTop: dimensions.spaces.small,
            }}
          >
            <Text>
              Por favor, nos informe pelo menos uma referência de transportadora
              ou embarcadora em que o motorista tenha prestado serviço
              recentemente.
            </Text>
          </Grid>
          <StyledForm
            onSubmit={() => handleRegister()}
            initialValues={{
              nomeObrigatorio: '',
              telefoneObrigatorio: '',
              nomeOpcional: '',
              telefoneOpcional: '',
            }}
            validationSchema={() => ({
              nomeObrigatorio: Yup.string().required('Nome é obrigatório!'),
              telefoneObrigatorio: Yup.string()
                .trim()
                .required('Telefone é obrigatório!')
                .min(13, 'Telefone inválido!')
                .max(15, 'Telefone inválido!'),
              nomeOpcional: Yup.string(),
              telefoneOpcional: Yup.string(),
            })}
            innerRef={formRef}
          >
            {formControl => (
              <Grid
                container
                spacing={2}
                style={{ marginBottom: dimensions.spaces.small }}
              >
                <Grid
                  container
                  justifyContent="flex-start"
                  alignItems="flex-end"
                  item
                  xs={12}
                  spacing={4}
                >
                  <Grid item xs={6} md={3}>
                    <Grid style={{ marginBottom: dimensions.spaces.small }}>
                      <Text>1ª Referência:</Text>
                    </Grid>
                    <StyledInput
                      label="Nome *"
                      placeholder="Digite o nome"
                      name="nomeObrigatorio"
                      type="text"
                      formControl={formControl}
                      errorMessage={errorMessageForm?.nomeObrigatorio}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StyledInput
                      label="Telefone *"
                      placeholder="DDD + Número"
                      name="telefoneObrigatorio"
                      formControl={formControl}
                      mask={value => phoneMask(value)}
                      maxLength={15}
                      errorMessage={errorMessageForm?.telefoneObrigatorio}
                    />
                  </Grid>
                </Grid>
                <Grid
                  container
                  item
                  justifyContent="flex-start"
                  alignItems="flex-end"
                  xs={12}
                  spacing={4}
                >
                  <Grid item xs={6} md={3}>
                    <Grid style={{ marginBottom: dimensions.spaces.small }}>
                      <Text>2ª Referência:</Text>
                    </Grid>
                    <StyledInput
                      label="Nome"
                      placeholder="Digite o nome"
                      name="nomeOpcional"
                      type="text"
                      formControl={formControl}
                      errorMessage={errorMessageForm?.nomeOpcional}
                    />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <StyledInput
                      label="Telefone"
                      placeholder="DDD + Número"
                      name="telefoneOpcional"
                      formControl={formControl}
                      mask={value => phoneMask(value)}
                      maxLength={15}
                      errorMessage={errorMessageForm?.telefoneOpcional}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
          </StyledForm>

          <SnackBar
            open={showingError}
            setOpen={setShowingError}
            severity="error"
            message={errorMessageSnackBar}
            horizontal="right"
          />
          <ModalSelectFrotista
            openModalSelectFrotista={openModalSelectFrotista}
            setOpenModalSelectFrotista={setOpenModalSelectFrotista}
          />
        </Container>
      </Scaffold>
    )
  }

export default ReferenciasProfissionais
